export type DashboardSettingKind = 'year' | 'percent' | 'integer' | 'decimal' | 'usd';

export type DashboardSettingSectionId =
  | 'reporting'
  | 'groupComparisons'
  | 'riskTriage'
  | 'schoolProfile'
  | 'budgetLaw'
  | 'teacherEquity';

export interface DashboardSettingFieldConfig {
  name: string;
  defaultValue: string;
  kind: DashboardSettingKind;
  section: DashboardSettingSectionId;
  label: string;
  helpText: string;
  min?: number;
  max?: number;
}

export const DASHBOARD_SETTING_SECTIONS: {
  id: DashboardSettingSectionId;
  title: string;
  description: string;
}[] = [
  {
    id: 'reporting',
    title: 'Reporting defaults',
    description: 'Default values pre-selected across report pages.',
  },
  {
    id: 'groupComparisons',
    title: 'Group Comparisons',
    description:
      'Peer variance outlier threshold for Group Comparisons triage (|variance vs peer median| ≥ outlier %).',
  },
  {
    id: 'riskTriage',
    title: 'Risk Triage',
    description: 'Anomaly score badge colors and indicator variance highlight styling.',
  },
  {
    id: 'schoolProfile',
    title: 'School Profile',
    description: 'Cost outlier ratio and governing completeness visual bands.',
  },
  {
    id: 'budgetLaw',
    title: 'Budget law / compliance',
    description: 'A+B governed budget split rule (minimum and warning band).',
  },
  {
    id: 'teacherEquity',
    title: 'Teacher Equity',
    description: 'Internal salary spread severity thresholds.',
  },
];

export const DASHBOARD_SETTING_FIELDS: DashboardSettingFieldConfig[] = [
  {
    name: 'Default Year',
    defaultValue: '2025',
    kind: 'year',
    section: 'reporting',
    label: 'Default year',
    helpText: 'Pre-selected in report year dropdowns for all users.',
  },
  {
    name: 'Group Comparisons Outlier Threshold',
    defaultValue: '25',
    kind: 'percent',
    section: 'groupComparisons',
    label: 'Outlier threshold',
    helpText: 'Schools flagged as outliers when |variance vs peer median| ≥ this %.',
    min: 1,
    max: 100,
  },
  {
    name: 'Risk Triage Anomaly Score High Threshold',
    defaultValue: '70',
    kind: 'integer',
    section: 'riskTriage',
    label: 'Anomaly score — high (red)',
    helpText: 'Scores at or above this value use the high-severity badge.',
    min: 0,
    max: 100,
  },
  {
    name: 'Risk Triage Anomaly Score Medium Threshold',
    defaultValue: '40',
    kind: 'integer',
    section: 'riskTriage',
    label: 'Anomaly score — medium (amber)',
    helpText: 'Scores at or above this (and below high) use the medium-severity badge.',
    min: 0,
    max: 100,
  },
  {
    name: 'Risk Triage Variance Highlight Threshold',
    defaultValue: '15',
    kind: 'percent',
    section: 'riskTriage',
    label: 'Variance highlight threshold',
    helpText: 'Indicator variance at or above this % is emphasized in the detail panel.',
    min: 1,
    max: 100,
  },
  {
    name: 'School Profile Cost Outlier Ratio Threshold',
    defaultValue: '1.25',
    kind: 'decimal',
    section: 'schoolProfile',
    label: 'Cost outlier ratio',
    helpText: 'School cost / peer average at or above this ratio is flagged (e.g. 1.25 = 25% above peer).',
    min: 0.01,
  },
  {
    name: 'School Profile Completeness Good Threshold',
    defaultValue: '80',
    kind: 'percent',
    section: 'schoolProfile',
    label: 'Completeness — good',
    helpText: 'Completeness at or above this % uses the good (green) band.',
    min: 1,
    max: 100,
  },
  {
    name: 'School Profile Completeness Warning Threshold',
    defaultValue: '50',
    kind: 'percent',
    section: 'schoolProfile',
    label: 'Completeness — warning',
    helpText: 'Completeness at or above this % (and below good) uses the warning (amber) band.',
    min: 1,
    max: 100,
  },
  {
    name: 'Budget Law A+B Minimum Percent',
    defaultValue: '65',
    kind: 'percent',
    section: 'budgetLaw',
    label: 'A+B minimum',
    helpText: 'Minimum A+B share for budget law compliance.',
    min: 1,
    max: 100,
  },
  {
    name: 'Budget Law A+B Warning Band Percent',
    defaultValue: '50',
    kind: 'percent',
    section: 'budgetLaw',
    label: 'A+B warning band',
    helpText: 'A+B share at or above this % (and below minimum) uses the warning band.',
    min: 1,
    max: 100,
  },
  {
    name: 'Teacher Equity Spread Severity Very High Ratio',
    defaultValue: '1.6',
    kind: 'decimal',
    section: 'teacherEquity',
    label: 'Severity — very high ratio',
    helpText: 'Spread / mean spread at or above this ratio is very high severity.',
    min: 0.01,
  },
  {
    name: 'Teacher Equity Spread Severity High Ratio',
    defaultValue: '1.2',
    kind: 'decimal',
    section: 'teacherEquity',
    label: 'Severity — high ratio',
    helpText: 'Spread / mean spread at or above this (and below very high) is high severity.',
    min: 0.01,
  },
  {
    name: 'Teacher Equity Spread Severity Very High Amount USD',
    defaultValue: '14000',
    kind: 'usd',
    section: 'teacherEquity',
    label: 'Severity — very high amount (USD)',
    helpText: 'Absolute spread fallback when mean spread ≤ 0.',
    min: 0,
  },
  {
    name: 'Teacher Equity Spread Severity High Amount USD',
    defaultValue: '9000',
    kind: 'usd',
    section: 'teacherEquity',
    label: 'Severity — high amount (USD)',
    helpText: 'Absolute spread fallback when mean spread ≤ 0 (below very high amount).',
    min: 0,
  },
];

/** Lookup map: setting name → default string value. */
export const DASHBOARD_SETTING_DEFAULTS: Record<string, string> = Object.fromEntries(
  DASHBOARD_SETTING_FIELDS.map((f) => [f.name, f.defaultValue])
);

export const DEFAULT_YEAR_SETTING_NAME = 'Default Year';

export const GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME = 'Group Comparisons Outlier Threshold';

export function fieldsForSection(section: DashboardSettingSectionId): DashboardSettingFieldConfig[] {
  return DASHBOARD_SETTING_FIELDS.filter((f) => f.section === section);
}

export function getFieldConfig(name: string): DashboardSettingFieldConfig | undefined {
  return DASHBOARD_SETTING_FIELDS.find((f) => f.name === name);
}
